import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ArrowsRightLeftIcon,
  BanknotesIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

import Layout from '../../components/layout/Layout'
import { Badge, Button, Progress, Spinner } from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import { useSubledgerRun } from './api'
import { formatCurrency } from './utils'

const navItems = [
  { label: 'Close Review', path: '/subledger-revenue-reconciler' },
  { label: 'Reconciling Items', path: '/subledger-revenue-reconciler/review' },
  { label: 'Source Evidence', path: '/subledger-revenue-reconciler/sources' },
  { label: 'NetSuite Exports', path: '/subledger-revenue-reconciler/exports' },
]

export default function SubledgerRevenueReconcilerLayout({ children }: { children: ReactNode }) {
  const location = useLocation()
  const runQuery = useSubledgerRun()
  const run = runQuery.data
  const varianceBase = Math.max(Math.abs(run?.summary.gl_credit_total ?? 0), 1)
  const variancePct = Math.min(Math.abs(run?.summary.net_variance ?? 0) / varianceBase * 100, 100)

  return (
    <Layout>
      <div className="h-full overflow-auto bg-gray-50 dark:bg-dark-bg">
        <div className="mx-auto flex w-full min-w-0 max-w-[1680px] flex-col gap-6 px-4 py-4 sm:px-6 sm:py-6">
          <header className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
            <div className="grid gap-0 xl:grid-cols-[minmax(0,1fr)_420px]">
              <div className="flex min-h-[188px] min-w-0 flex-col justify-between gap-8 p-5 sm:p-7">
                <div className="max-w-3xl min-w-0">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <Badge variant="default">EC-03e</Badge>
                    <Badge variant="secondary">Revenue close</Badge>
                    {run?.summary.pending_review_count ? (
                      <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                        {run.summary.pending_review_count} review items
                      </Badge>
                    ) : null}
                    {run?.data_source.redshift_schema ? (
                      <Badge variant="secondary" className="inline-block max-w-full whitespace-normal break-all">
                        Redshift: {run.data_source.redshift_schema}
                      </Badge>
                    ) : null}
                  </div>
                  <h1 className="break-words font-title text-2xl text-charcoal dark:text-gray-100 sm:text-3xl">
                    Sub-Ledger Revenue Reconciler
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                    Revenue subledger-to-GL tie-out, EC-03d awareness, Controller review routing, and balanced correction exports.
                  </p>
                </div>

                <nav className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0">
                  {navItems.map(item => {
                    const active = location.pathname === item.path
                    return (
                      <Link key={item.path} to={item.path} className="shrink-0">
                        <Button
                          variant={active ? 'brand' : 'ghost'}
                          size="sm"
                          className={cn('min-h-10 whitespace-nowrap', !active && 'text-gray-600 dark:text-gray-300')}
                        >
                          {item.label}
                        </Button>
                      </Link>
                    )
                  })}
                </nav>
              </div>

              <aside className="border-t border-gray-200 bg-gray-50 p-5 dark:border-dark-border dark:bg-dark-surface-2 sm:p-6 xl:border-l xl:border-t-0">
                {runQuery.isLoading ? (
                  <div className="flex h-full min-h-[140px] items-center justify-center">
                    <Spinner size="lg" />
                  </div>
                ) : run ? (
                  <div className="flex h-full flex-col justify-between gap-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Period
                        </p>
                        <p className="mt-1 text-lg font-semibold text-charcoal dark:text-gray-100">
                          {run.period}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          Entity
                        </p>
                        <p className="mt-1 truncate text-lg font-semibold text-charcoal dark:text-gray-100">
                          {run.entity.replace(', Inc.', '')}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex flex-wrap items-end justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            Net variance
                          </p>
                          <p className="mt-1 text-2xl font-semibold text-charcoal dark:text-gray-100 sm:text-3xl">
                            {formatCurrency(run.summary.net_variance, true)}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5 text-sm text-amber-700 dark:text-amber-300">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          Review required
                        </div>
                      </div>
                      <Progress value={variancePct} className="mt-4 h-2" />
                    </div>

                    <div className="grid gap-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <BanknotesIcon className="h-4 w-4" />
                          GL credits
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {formatCurrency(run.summary.gl_credit_total, true)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <DocumentTextIcon className="h-4 w-4" />
                          NetSuite JE
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {run.summary.journal_balanced ? 'Balanced' : 'Mismatch'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <ArrowsRightLeftIcon className="h-4 w-4" />
                          Auto-posted
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {run.summary.auto_posted_account_count} accounts
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <CheckCircleIcon className="h-4 w-4" />
                          Awareness
                        </span>
                        <span className="font-medium text-charcoal dark:text-gray-100">
                          {run.summary.auto_aware_count} flags
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full min-h-[140px] items-center text-sm text-gray-500 dark:text-gray-400">
                    Sub-ledger data is waiting on the close calendar gate.
                  </div>
                )}
              </aside>
            </div>
          </header>

          {children}
        </div>
      </div>
    </Layout>
  )
}
